import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { normalizeMobileNumber, formatMobileForDisplay, formatNumberPersian, convertPersianToEnglish } from '../utils/mobileUtils';
import { formatPrice } from '../utils/priceUtils';
import { formatPersianDateShort, formatPersianDateTime, persianDateToISO, isoToPersianDate } from '../utils/dateUtils';
import { getImageUrl } from '../utils/urlUtils';
import { getOrderStatusLabel, ORDER_STATUS_LIST } from '../utils/orderStatusUtils';
import { apiFetch } from '../utils/api';
import { userFacingApiError, userFacingMessageFromResponse, persianOrNull } from '../utils/userMessages';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './Dashboard.css';

function Dashboard() {
  const { user, logout, updateUser } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { showNotification } = useNotification();
  const { refreshSettings } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // Helper function to close sidebar only on mobile
  const closeSidebarIfMobile = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]); // For admin
  const [payments, setPayments] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [siteSettings, setSiteSettings] = useState([]);
  const [editingSetting, setEditingSetting] = useState(null);
  const [settingValue, setSettingValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropImageType, setCropImageType] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 90, height: 90, x: 5, y: 5 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [gallerySelectionMode, setGallerySelectionMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingDiscountCode, setEditingDiscountCode] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    imageUrl: '',
    categoryId: ''
  });
  const [productImages, setProductImages] = useState([]);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });
  const [addressFormData, setAddressFormData] = useState({
    province: '',
    city: '',
    postalCode: '',
    fullAddress: '',
    plaque: '',
    unit: '',
    receiverName: '',
    receiverMobile: ''
  });
  const [discountFormData, setDiscountFormData] = useState({
    code: '',
    discountPercent: '',
    startDate: '',
    endDate: '',
    isActive: true,
    maxUses: '',
    minPurchaseAmount: '',
    description: ''
  });
  const [gatewayTestAmount, setGatewayTestAmount] = useState('');
  const [gatewayTestLoading, setGatewayTestLoading] = useState(false);
  const [inquiryTrackId, setInquiryTrackId] = useState('');
  const [inquiryResult, setInquiryResult] = useState(null);
  const [inquiryLoading, setInquiryLoading] = useState(false);

  const isProgrammer = user?.role?.name === 'programmer';
  const isAdmin = user?.role?.name === 'admin';
  const isProgrammerOrAdmin = isProgrammer || isAdmin;

  // Set page title - ensure it's always "پنل کاربری ولوریا" when on dashboard
  useEffect(() => {
    const dashboardTitle = 'پنل کاربری ولوریا';
    
    // Set title immediately
    document.title = dashboardTitle;
    
    // Also set it after a short delay to override any other changes (like SiteSettingsContext)
    const timeoutId = setTimeout(() => {
      document.title = dashboardTitle;
    }, 300);
    
    // Watch for title changes and restore it if we're on dashboard
    const titleElement = document.querySelector('title');
    if (titleElement) {
      const observer = new MutationObserver(() => {
        if (window.location.pathname.includes('/dashboard') && document.title !== dashboardTitle) {
          document.title = dashboardTitle;
        }
      });
      
      observer.observe(titleElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
      
      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    }
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Fetch latest user data when component mounts
  useEffect(() => {
    if (user?.mobile) {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mobile]);

  const fetchUserData = async () => {
    try {
      const response = await apiFetch(`/api/auth/user/${encodeURIComponent(user.mobile)}`);
      if (response.ok) {
        const userData = await response.json();
        updateUser(userData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (activeTab === 'products' && isProgrammerOrAdmin) {
      fetchProducts();
      fetchCategories();
    } else if (activeTab === 'users' && isProgrammerOrAdmin) {
      fetchUsers();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'addresses') {
      fetchAddresses();
    } else if (activeTab === 'discountCodes' && isProgrammerOrAdmin) {
      fetchDiscountCodes();
    } else if (activeTab === 'categories' && isProgrammerOrAdmin) {
      fetchCategories();
    } else if (activeTab === 'files' && isProgrammerOrAdmin) {
      fetchFiles();
    } else if (activeTab === 'allOrders' && isProgrammerOrAdmin) {
      fetchAllOrders();
    } else if (activeTab === 'payments' && isProgrammerOrAdmin) {
      fetchPayments();
    } else if (activeTab === 'siteSettings' && isProgrammer) {
      fetchSiteSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user, isProgrammerOrAdmin, isProgrammer]);

  useEffect(() => {
    const handleSwitchTab = (event) => {
      if (event.detail === 'addresses') {
        setActiveTab('addresses');
      }
    };
    window.addEventListener('switchTab', handleSwitchTab);
    return () => window.removeEventListener('switchTab', handleSwitchTab);
  }, []);

  useEffect(() => {
    const tab = location.state && location.state.activeTab;
    if (tab && typeof tab === 'string') {
      setActiveTab(tab);
      navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);


  const fetchProducts = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch categories and files when showing product form
  useEffect(() => {
    if (showForm && isProgrammerOrAdmin) {
      fetchCategories();
      fetchFiles();
    }
  }, [showForm, isProgrammerOrAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await apiFetch('/api/users');
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };


  const fetchSiteSettings = async () => {
    try {
      const response = await apiFetch('/api/site-settings/admin/all');
      if (response.ok) {
        const data = await response.json();
// sort by key for stable order
        const sortedData = data.sort((a, b) => {
          const order = [
            'site_name',
            'site_description',
            'favicon_url',
            'logo_url',
            'telegram_channel',
            'instagram_channel',
            'whatsapp_channel',
            'bale_channel',
            'rubika_channel',
            'about_us',
            'support',
            'zibal_merchant_id',
            'zibal_footer_url',
            'sms_template_id',
            'enamad_inquiry_url'
          ];
          const indexA = order.indexOf(a.key);
          const indexB = order.indexOf(b.key);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        setSiteSettings(sortedData);
      } else {
        let msg = 'خطا در دریافت تنظیمات';
        try {
          const errorData = await response.json();
          msg = userFacingApiError(errorData, msg);
        } catch {
          /* ignore */
        }
        showNotification(msg, 'error');
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
      showNotification('خطا در دریافت تنظیمات', 'error');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/api/orders/user/${user.mobile}`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/api/addresses/user/${user.mobile}`);
      if (response.ok) {
        const data = await response.json();
        setAddresses(data);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountCodes = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/discount-codes');
      if (response.ok) {
        const data = await response.json();
        setDiscountCodes(data);
      }
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert Persian digits to English for numeric fields
    let processedValue = value;
    if (name === 'price' || name === 'stock') {
      processedValue = convertPersianToEnglish(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/files/list');
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      showNotification('فایل خیلی بزرگ است. حداکثر اندازه مجاز 20 مگابایت است.', 'error');
      e.target.value = '';
      return;
    }

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      showNotification('فقط فایل‌های تصویری مجاز هستند.', 'error');
      e.target.value = '';
      return;
    }

    // Show crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setCropImageType(file.type); // Store original file type to preserve transparency
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext('2d');

    // Clear canvas with transparent background (important for PNG)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      // Preserve original format (PNG for transparency, JPG for others)
      const outputType = cropImageType && cropImageType.includes('png') ? 'image/png' : 'image/jpeg';
      const quality = outputType === 'image/png' ? undefined : 0.95;
      
      canvas.toBlob((blob) => {
        resolve(blob);
      }, outputType, quality);
    });
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) {
      showNotification('لطفاً ناحیه مورد نظر را انتخاب کنید', 'error');
      return;
    }

    setLoading(true);
    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      const isPng = cropImageType && cropImageType.includes('png');
      const extension = isPng ? '.png' : '.jpg';
      const mimeType = isPng ? 'image/png' : 'image/jpeg';
      const uniqueName = `cropped-${Date.now()}-${Math.random().toString(36).slice(2, 9)}${extension}`;
      const croppedFile = new File([croppedBlob], uniqueName, { type: mimeType });

      const formData = new FormData();
      formData.append('file', croppedFile);

      const response = await apiFetch('/api/files/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showNotification(userFacingApiError(errorData, 'خطا در آپلود فایل'), 'error');
        return;
      }

      await fetchFiles();
      showNotification('عکس با موفقیت آپلود شد', 'success');
      setShowCropModal(false);
      setCropImage(null);
      setCropImageType(null);
      setCrop({ unit: '%', width: 90, height: 90 });
      setCompletedCrop(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      showNotification('خطا در آپلود عکس', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    // This is now handled by handleFileSelect
    handleFileSelect(e);
  };

  const handleFileDelete = async (filename) => {
    try {
      const response = await apiFetch(`/api/files/${filename}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchFiles();
        showNotification('عکس با موفقیت حذف شد', 'success');
      } else {
        showNotification('خطا در حذف عکس', 'error');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showNotification('خطا در حذف عکس', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === '0') return '0 B';
    const size = parseInt(bytes);
    if (size < 1024) return formatNumberPersian(size) + ' B';
    if (size < 1024 * 1024) return formatNumberPersian(Math.round(size / 1024)) + ' KB';
    return formatNumberPersian(Math.round(size / (1024 * 1024))) + ' MB';
  };

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/orders');
      if (response.ok) {
        const data = await response.json();
        setAllOrders(data);
      }
    } catch (error) {
      console.error('Error fetching all orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/payments');
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderStatusUpdate = async (orderId, newStatus) => {
    try {
      const response = await apiFetch(`/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        await fetchAllOrders();
        showNotification('وضعیت سفارش با موفقیت به‌روزرسانی شد', 'success');
      } else {
        showNotification('خطا در به‌روزرسانی وضعیت سفارش', 'error');
      }
    } catch (error) {
      showNotification('خطا در به‌روزرسانی وضعیت سفارش', 'error');
    }
  };

  const handlePaymentStatusUpdate = async (paymentId, newStatus) => {
    try {
      const response = await apiFetch(`/api/payments/${paymentId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        await fetchPayments();
        showNotification('وضعیت پرداخت با موفقیت به‌روزرسانی شد', 'success');
      } else {
        showNotification('خطا در به‌روزرسانی وضعیت پرداخت', 'error');
      }
    } catch (error) {
      showNotification('خطا در به‌روزرسانی وضعیت پرداخت', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Find category object from categoryId
      const selectedCategory = categories.find(cat => cat.id === parseInt(formData.categoryId));
      
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(convertPersianToEnglish(formData.price)),
        stock: parseInt(convertPersianToEnglish(formData.stock)),
        imageUrl: productImages.length > 0 ? productImages[0].imageUrl : formData.imageUrl,
        category: selectedCategory ? { id: selectedCategory.id } : null
      };

      if (editingProduct) {
        const response = await apiFetch(`/api/products/${editingProduct.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(productData)
        });

        if (response.ok) {
          await response.json();
          // Save product images (only if there are images to save)
          if (productImages.length > 0) {
            try {
              // Delete old images first (only if there are old images)
              const oldImagesResponse = await apiFetch(`/api/products/${editingProduct.id}/images`);
              if (oldImagesResponse.ok) {
                const oldImages = await oldImagesResponse.json();
                if (oldImages && oldImages.length > 0) {
                  for (const oldImg of oldImages) {
                    try {
                      const deleteResponse = await apiFetch(`/api/products/images/${oldImg.id}`, { method: 'DELETE' });
                      if (!deleteResponse.ok) {
                        console.warn('Failed to delete old image:', oldImg.id);
                      }
                    } catch (e) {
                      console.warn('Error deleting image:', e);
                      // Continue even if deletion fails
                    }
                  }
                }
              }
              
              // Add all additional images (first one is already set as imageUrl, add the rest)
              if (productImages.length > 1) {
                for (let i = 1; i < productImages.length; i++) {
                  try {
                    const addResponse = await apiFetch(`/api/products/${editingProduct.id}/images`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        imageUrl: productImages[i].imageUrl,
                        displayOrder: i
                      })
                    });
                    if (!addResponse.ok) {
                      console.warn('Failed to add image:', productImages[i].imageUrl);
                    }
                  } catch (e) {
                    console.warn('Error adding image:', e);
                    // Continue even if adding fails
                  }
                }
              }
            } catch (e) {
              console.error('Error managing product images:', e);
              // Don't fail the whole operation if image management fails
            }
          }
          await fetchProducts();
          resetForm();
          showNotification('محصول با موفقیت به‌روزرسانی شد', 'success');
        } else {
          const msg = await userFacingMessageFromResponse(response, 'خطا در به‌روزرسانی محصول');
          console.error('Error updating product:', msg);
          showNotification(msg, 'error');
        }
      } else {
        const response = await apiFetch('/api/products', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(productData)
        });

        if (response.ok) {
          const newProduct = await response.json();
          // Save additional images
          if (productImages.length > 1) {
            for (let i = 1; i < productImages.length; i++) {
              try {
                await apiFetch(`/api/products/${newProduct.id}/images`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    imageUrl: productImages[i].imageUrl,
                    displayOrder: i
                  })
                });
              } catch (e) {
                console.warn('Error adding image:', e);
                // Continue even if adding fails
              }
            }
          }
          await fetchProducts();
          resetForm();
          showNotification('محصول با موفقیت اضافه شد', 'success');
        } else {
          const msg = await userFacingMessageFromResponse(response, 'خطا در افزودن محصول');
          console.error('Error creating product:', msg);
          showNotification(msg, 'error');
        }
      }
    } catch (error) {
      showNotification('خطا در ذخیره محصول', 'error');
    }
  };

  const handleEdit = async (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: formatNumberPersian(product.price || ''),
      stock: formatNumberPersian(product.stock || ''),
      imageUrl: product.imageUrl || '',
      categoryId: product.category?.id || ''
    });
    
    // Fetch product images
    try {
      const imagesResponse = await apiFetch(`/api/products/${product.id}/images`);
      if (imagesResponse.ok) {
        const images = await imagesResponse.json();
        // Combine main image (imageUrl) with additional images
        const allImages = [];
        if (product.imageUrl) {
          allImages.push({ imageUrl: getImageUrl(product.imageUrl), isMain: true });
        }
        if (images && images.length > 0) {
          allImages.push(...images.map(img => ({ imageUrl: getImageUrl(img.imageUrl), isMain: false })));
        }
        setProductImages(allImages);
      } else {
        // If no additional images, use main image if available
        if (product.imageUrl) {
          setProductImages([{ imageUrl: getImageUrl(product.imageUrl), isMain: true }]);
        } else {
          setProductImages([]);
        }
      }
    } catch (error) {
      // On error, use main image if available
      if (product.imageUrl) {
        setProductImages([{ imageUrl: getImageUrl(product.imageUrl), isMain: true }]);
      } else {
        setProductImages([]);
      }
    }
    
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید این محصول را حذف کنید؟')) {
      try {
        const response = await apiFetch(`/api/products/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await fetchProducts();
          showNotification('محصول با موفقیت حذف شد', 'success');
        }
      } catch (error) {
        showNotification('خطا در حذف محصول', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      stock: '',
      imageUrl: '',
      categoryId: ''
    });
    setProductImages([]);
    setEditingProduct(null);
    setShowForm(false);
  };

  const getPlaceholderImage = (text = 'No Image', width = 50, height = 50) => {
    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="${width}" height="${height}" fill="#f0f0f0"/>
        <text x="50%" y="50%" font-family="Vazirmatn, sans-serif" font-size="${Math.min(width, height) / 5}" fill="#999" text-anchor="middle" dominant-baseline="middle">${text}</text>
      </svg>
    `.trim();
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = async () => { try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch (_) {} logout(); navigate('/'); };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    
    // Normalize receiver mobile number
    const normalizedReceiverMobile = normalizeMobileNumber(addressFormData.receiverMobile);
    if (!normalizedReceiverMobile) {
      showNotification('شماره موبایل گیرنده نامعتبر است', 'error');
      return;
    }
    
    try {
      const addressData = {
        ...addressFormData,
        receiverMobile: normalizedReceiverMobile,
        mobile: user.mobile
      };

      if (editingAddress) {
        const response = await apiFetch(`/api/addresses/${editingAddress.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addressData)
        });

        if (response.ok) {
          await fetchAddresses();
          setShowAddressForm(false);
          setEditingAddress(null);
          showNotification('آدرس با موفقیت به‌روزرسانی شد', 'success');
        }
      } else {
        const response = await apiFetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addressData)
        });

        if (response.ok) {
          await fetchAddresses();
          setShowAddressForm(false);
          setAddressFormData({
            province: '',
            city: '',
            postalCode: '',
            fullAddress: '',
            plaque: '',
            unit: '',
            receiverName: user?.name || '',
            receiverMobile: user?.mobile || ''
          });
          showNotification('آدرس با موفقیت اضافه شد', 'success');
        } else if (response.status === 409) {
          showNotification('این آدرس قبلاً ثبت شده است', 'error');
        } else {
          showNotification('خطا در اضافه کردن آدرس', 'error');
        }
      }
    } catch (error) {
      showNotification('خطا در ذخیره آدرس', 'error');
    }
  };

  const handleDeleteAddress = async (id) => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید این آدرس را حذف کنید؟')) {
      try {
        const response = await apiFetch(`/api/addresses/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await fetchAddresses();
          showNotification('آدرس با موفقیت حذف شد', 'success');
        }
      } catch (error) {
        showNotification('خطا در حذف آدرس', 'error');
      }
    }
  };


  const handleDiscountCodeSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert Persian dates to ISO format for backend
      let startDateISO = discountFormData.startDate;
      let endDateISO = discountFormData.endDate;
      
      // If dates are in Persian format, convert them
      if (discountFormData.startDate && discountFormData.startDate.includes('/')) {
        startDateISO = persianDateToISO(discountFormData.startDate);
      }
      if (discountFormData.endDate && discountFormData.endDate.includes('/')) {
        endDateISO = persianDateToISO(discountFormData.endDate);
      }
      
      const discountData = {
        code: discountFormData.code,
        discountPercent: discountFormData.discountPercent ? parseFloat(convertPersianToEnglish(discountFormData.discountPercent)) : null,
        maxUses: discountFormData.maxUses ? parseInt(convertPersianToEnglish(discountFormData.maxUses)) : null,
        minPurchaseAmount: discountFormData.minPurchaseAmount ? parseFloat(convertPersianToEnglish(discountFormData.minPurchaseAmount)) : null,
        startDate: startDateISO,
        endDate: endDateISO,
        isActive: discountFormData.isActive !== false,
        description: discountFormData.description || ''
      };

      if (editingDiscountCode) {
        const response = await apiFetch(`/api/discount-codes/${editingDiscountCode.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(discountData)
        });

        if (response.ok) {
          await fetchDiscountCodes();
          setShowDiscountForm(false);
          setEditingDiscountCode(null);
          setDiscountFormData({
            code: '',
            discountPercent: '',
            startDate: '',
            endDate: '',
            isActive: true,
            maxUses: '',
            minPurchaseAmount: '',
            description: ''
          });
          showNotification('کد تخفیف با موفقیت به‌روزرسانی شد', 'success');
        } else {
          const msg = await userFacingMessageFromResponse(response, 'خطا در به‌روزرسانی کد تخفیف');
          showNotification(msg, 'error');
        }
      } else {
        const response = await apiFetch('/api/discount-codes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(discountData)
        });

        if (response.ok) {
          await fetchDiscountCodes();
          setShowDiscountForm(false);
          setDiscountFormData({
            code: '',
            discountPercent: '',
            startDate: '',
            endDate: '',
            isActive: true,
            maxUses: '',
            minPurchaseAmount: '',
            description: ''
          });
          showNotification('کد تخفیف با موفقیت اضافه شد', 'success');
        } else {
          const msg = await userFacingMessageFromResponse(response, 'خطا در اضافه کردن کد تخفیف');
          showNotification(msg, 'error');
        }
      }
    } catch (error) {
      showNotification('خطا در ذخیره کد تخفیف', 'error');
    }
  };

  const handleDeleteDiscountCode = async (id) => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید این کد تخفیف را حذف کنید؟')) {
      try {
        const response = await apiFetch(`/api/discount-codes/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await fetchDiscountCodes();
          showNotification('کد تخفیف با موفقیت حذف شد', 'success');
        }
      } catch (error) {
        showNotification('خطا در حذف کد تخفیف', 'error');
      }
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        const response = await apiFetch(`/api/categories/${editingCategory.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(categoryFormData)
        });

        if (response.ok) {
          await fetchCategories();
          // Refresh products after category update
          if (activeTab === 'products') {
            await fetchProducts();
          }
          setShowCategoryForm(false);
          setEditingCategory(null);
          setCategoryFormData({ name: '', description: '' });
          showNotification('دسته‌بندی با موفقیت به‌روزرسانی شد', 'success');
        } else {
          showNotification('خطا در به‌روزرسانی دسته‌بندی (احتمالاً نام تکراری است)', 'error');
        }
      } else {
        const response = await apiFetch('/api/categories', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(categoryFormData)
        });

        if (response.ok) {
          await fetchCategories();
          setShowCategoryForm(false);
          setCategoryFormData({ name: '', description: '' });
          showNotification('دسته‌بندی با موفقیت اضافه شد', 'success');
        } else {
          showNotification('خطا در اضافه کردن دسته‌بندی (احتمالاً نام تکراری است)', 'error');
        }
      }
    } catch (error) {
      showNotification('خطا در ذخیره دسته‌بندی', 'error');
    }
  };

  const handleCategoryEdit = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || '',
      description: category.description || ''
    });
    setShowCategoryForm(true);
  };

  const handleCategoryDelete = async (id) => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید این دسته‌بندی را حذف کنید؟')) {
      try {
        const response = await apiFetch(`/api/categories/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await fetchCategories();
          // Refresh products after category deletion
          if (activeTab === 'products') {
            await fetchProducts();
          }
          showNotification('دسته‌بندی با موفقیت حذف شد', 'success');
        } else {
          showNotification('خطا در حذف دسته‌بندی', 'error');
        }
      } catch (error) {
        showNotification('خطا در حذف دسته‌بندی', 'error');
      }
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="dashboard-section">
            <h2>پروفایل من</h2>
            <div className="profile-info">
              <p><strong>نام:</strong> {user?.name || 'تعریف نشده'}</p>
              <p><strong>شماره موبایل:</strong> {formatMobileForDisplay(user?.mobile)}</p>
              <p><strong>نقش:</strong> {
                user?.role?.name === 'programmer' ? 'برنامه‌نویس' :
                user?.role?.name === 'admin' ? 'مدیر' : 
                user?.role?.name === 'support' ? 'پشتیبانی' : 
                'کاربر'
              }</p>
              <button 
                className="btn-primary"
                onClick={() => navigate('/profile')}
              >
                ویرایش پروفایل
              </button>
            </div>
          </div>
        );

      case 'orders':
        return (
          <div className="dashboard-section">
            <h2>سفارش‌های من</h2>
            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : orders.length === 0 ? (
              <p>شما هنوز سفارشی ثبت نکرده‌اید.</p>
            ) : (
              <div className="orders-list">
                {orders.map(order => (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <span>سفارش #{formatNumberPersian(order.id)}</span>
                      <span className={`status-badge ${order.status?.toLowerCase()}`}>
                        {getOrderStatusLabel(order.status)}
                      </span>
                    </div>
                    <p>مبلغ کل: {formatPrice(order.totalAmount)}</p>
                    <p>تاریخ: {formatPersianDateShort(order.createdAt)}</p>
                    <button 
                      className="btn-secondary"
                      onClick={() => navigate(`/invoice/${order.id}`)}
                    >
                      مشاهده فاکتور
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'products':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>مدیریت محصولات</h2>
              <button 
                className="btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                + افزودن محصول جدید
              </button>
            </div>

            {showForm && (
              <div className="product-form-container">
                <div className="product-form">
                  <div className="form-header">
                    <h3>{editingProduct ? 'ویرایش محصول' : 'افزودن محصول جدید'}</h3>
                    <button className="close-btn" onClick={resetForm}>×</button>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>نام محصول</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>توضیحات</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>قیمت (ریال)</label>
                        <input
                          type="text"
                          name="price"
                          value={formData.price}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9۰-۹.]/g, '');
                            handleInputChange({ target: { name: 'price', value } });
                          }}
                          placeholder="قیمت (ریال)"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>موجودی</label>
                        <input
                          type="text"
                          name="stock"
                          value={formData.stock}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9۰-۹]/g, '');
                            handleInputChange({ target: { name: 'stock', value } });
                          }}
                          placeholder="موجودی - مثال: ۵۰"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>دسته‌بندی</label>
                      <select
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">انتخاب دسته‌بندی</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>تصاویر محصول</label>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShowGalleryModal(true)}
                        style={{marginBottom: '15px'}}
                      >
                        انتخاب از گالری
                      </button>
                      {productImages.length > 0 && (
                        <div style={{marginTop: '15px', border: '1px solid #ddd', padding: '15px', borderRadius: '8px'}}>
                          <strong style={{display: 'block', marginBottom: '10px'}}>عکس‌های انتخاب شده ({formatNumberPersian(productImages.length)}):</strong>
                          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '15px'}}>
                            {productImages.map((img, index) => (
                              <div
                                key={index}
                                onClick={() => {
                                  // Move clicked image to first position (make it main)
                                  setProductImages(prev => {
                                    const newImages = [...prev];
                                    const clickedImage = newImages[index];
                                    newImages.splice(index, 1);
                                    newImages.unshift(clickedImage);
                                    return newImages;
                                  });
                                }}
                                style={{
                                  position: 'relative',
                                  border: index === 0 ? '3px solid #0C8EF4' : '1px solid #ddd',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  padding: '2px',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => {
                                  if (index !== 0) {
                                    e.currentTarget.style.borderColor = '#0C8EF4';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (index !== 0) {
                                    e.currentTarget.style.borderColor = '#ddd';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }
                                }}
                              >
                                {index === 0 && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    background: '#0C8EF4',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    zIndex: 10
                                  }}>عکس اصلی</div>
                                )}
                                {index !== 0 && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    background: 'rgba(0,0,0,0.6)',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    zIndex: 10
                                  }}>کلیک برای اصلی</div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent making it main when clicking delete
                                    setProductImages(prev => {
                                      const newImages = prev.filter((_, i) => i !== index);
                                      return newImages;
                                    });
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '5px',
                                    left: '5px',
                                    background: '#ff4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: '1',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                  }}
                                >
                                  ×
                                </button>
                                <div style={{
                                  width: '100%',
                                  aspectRatio: '1',
                                  background: 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <img 
                                    src={getImageUrl(img.imageUrl)}
                                    alt={`Product image ${index + 1}`}
                                    style={{width: '100%', height: '100%', objectFit: 'contain', background: 'transparent'}}
                                    onError={(e) => {
                                      e.target.src = getPlaceholderImage('Error', 120, 120);
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingProduct ? 'ذخیره تغییرات' : 'افزودن محصول'}
                      </button>
                      <button type="button" className="btn-cancel" onClick={resetForm} style={{width: 'auto', minWidth: 'fit-content', flex: '0 0 auto'}}>
                        انصراف
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="products-table-container">
              <h3>لیست محصولات ({formatNumberPersian(products.length)})</h3>
              <div className="products-table">
                <table>
                  <thead>
                    <tr>
                      <th>تصویر</th>
                      <th>نام</th>
                      <th>دسته‌بندی</th>
                      <th>قیمت</th>
                      <th>موجودی</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <tr key={product.id}>
                        <td>
                          <img 
                            src={getImageUrl(product.imageUrl) || getPlaceholderImage('No Image', 50, 50)} 
                            alt={product.name}
                            className="product-thumbnail"
                            onError={(e) => {
                              e.target.src = getPlaceholderImage('No Image', 50, 50);
                            }}
                          />
                        </td>
                        <td>
                          <div className="product-name-cell">
                            <strong>{product.name}</strong>
                            <span className="product-description-cell">{product.description}</span>
                          </div>
                        </td>
                        <td>{product.category?.name || product.category || '-'}</td>
                        <td>{formatPrice(product.price)}</td>
                        <td>
                          <span className={`stock-badge ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                            {formatNumberPersian(product.stock)}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-edit"
                              onClick={() => handleEdit(product)}
                            >
                              ویرایش
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => handleDelete(product.id)}
                            >
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'users':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <h2>مدیریت کاربران</h2>
            <div className="users-table-container">
              <table>
                <thead>
                  <tr>
                    <th>نام</th>
                    <th>شماره موبایل</th>
                    <th>نقش</th>
                    <th>وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.name || 'تعریف نشده'}</td>
                      <td>{formatMobileForDisplay(u.mobile)}</td>
                      <td>{
                        u.role?.name === 'programmer' ? 'برنامه‌نویس' :
                        u.role?.name === 'admin' ? 'مدیر' : 
                        u.role?.name === 'support' ? 'پشتیبانی' : 
                        'کاربر'
                      }</td>
                      <td>{u.isVerified ? 'تایید شده' : 'تایید نشده'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'addresses':
        return (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>آدرس‌های من</h2>
              <button 
                className="btn-primary"
                onClick={() => {
                  setAddressFormData({
                    province: '',
                    city: '',
                    postalCode: '',
                    fullAddress: '',
                    plaque: '',
                    unit: '',
                    receiverName: '',
                    receiverMobile: '',
                    isDefault: false
                  });
                  setEditingAddress(null);
                  setShowAddressForm(true);
                }}
              >
                + افزودن آدرس جدید
              </button>
            </div>

            {showAddressForm && (
              <div className="address-form-container">
                <div className="address-form">
                  <div className="form-header">
                    <h3>{editingAddress ? 'ویرایش آدرس' : 'افزودن آدرس جدید'}</h3>
                    <button className="close-btn" onClick={() => {
                      setShowAddressForm(false);
                      setEditingAddress(null);
                    }}>×</button>
                  </div>
                  <form onSubmit={handleAddressSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>استان</label>
                        <input
                          type="text"
                          value={addressFormData.province}
                          onChange={(e) => setAddressFormData({...addressFormData, province: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>شهر</label>
                        <input
                          type="text"
                          value={addressFormData.city}
                          onChange={(e) => setAddressFormData({...addressFormData, city: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>کد پستی</label>
                        <input
                          type="text"
                          value={addressFormData.postalCode}
                          onChange={(e) => setAddressFormData({...addressFormData, postalCode: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>شماره تماس گیرنده</label>
                        <input
                          type="tel"
                          value={addressFormData.receiverMobile}
                          onChange={(e) => {
                            // Allow both Persian and English digits
                            const value = e.target.value.replace(/[^0-9۰-۹]/g, '');
                            setAddressFormData({...addressFormData, receiverMobile: value});
                          }}
                          placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                          maxLength="11"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>نام گیرنده</label>
                      <input
                        type="text"
                        value={addressFormData.receiverName}
                        onChange={(e) => setAddressFormData({...addressFormData, receiverName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>آدرس کامل</label>
                      <textarea
                        value={addressFormData.fullAddress}
                        onChange={(e) => setAddressFormData({...addressFormData, fullAddress: e.target.value})}
                        rows="4"
                        required
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>پلاک</label>
                        <input
                          type="text"
                          value={addressFormData.plaque}
                          onChange={(e) => setAddressFormData({...addressFormData, plaque: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>واحد</label>
                        <input
                          type="text"
                          value={addressFormData.unit}
                          onChange={(e) => setAddressFormData({...addressFormData, unit: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingAddress ? 'ذخیره تغییرات' : 'افزودن آدرس'}
                      </button>
                      <button type="button" className="btn-cancel" onClick={() => {
                        setShowAddressForm(false);
                        setEditingAddress(null);
                      }} style={{width: 'auto', minWidth: 'fit-content', flex: '0 0 auto'}}>
                        انصراف
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : addresses.length === 0 ? (
              <p>شما هنوز آدرسی ثبت نکرده‌اید.</p>
            ) : (
              <div className="addresses-list">
                {addresses.map(address => (
                  <div key={address.id} className="address-card">
                    <div className="address-header">
                      <h4>{address.receiverName}</h4>
                    </div>
                    <p><strong>استان:</strong> {address.province}</p>
                    <p><strong>شهر:</strong> {address.city}</p>
                    <p><strong>کد پستی:</strong> {address.postalCode}</p>
                    <p><strong>آدرس:</strong> {address.fullAddress}</p>
                    {address.plaque && <p><strong>پلاک:</strong> {address.plaque}</p>}
                    {address.unit && <p><strong>واحد:</strong> {address.unit}</p>}
                    <p><strong>شماره تماس:</strong> {formatMobileForDisplay(address.receiverMobile)}</p>
                    <div className="address-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => {
                          setEditingAddress(address);
                          setAddressFormData({
                            province: address.province || '',
                            city: address.city || '',
                            postalCode: address.postalCode || '',
                            fullAddress: address.fullAddress || '',
                            plaque: address.plaque || '',
                            unit: address.unit || '',
                            receiverName: address.receiverName || '',
                            receiverMobile: address.receiverMobile || '',
                            isDefault: address.isDefault || false
                          });
                          setShowAddressForm(true);
                        }}
                      >
                        ویرایش
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteAddress(address.id)}
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'discountCodes':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>مدیریت کدهای تخفیف</h2>
              <button 
                className="btn-primary"
                onClick={() => {
                  setDiscountFormData({
                    code: '',
                    discountPercent: '',
                    startDate: '',
                    endDate: '',
                    isActive: true,
                    maxUses: '',
                    minPurchaseAmount: '',
                    description: ''
                  });
                  setEditingDiscountCode(null);
                  setShowDiscountForm(true);
                }}
              >
                + افزودن کد تخفیف جدید
              </button>
            </div>

            {showDiscountForm && (
              <div className="address-form-container">
                <div className="address-form">
                  <div className="form-header">
                    <h3>{editingDiscountCode ? 'ویرایش کد تخفیف' : 'افزودن کد تخفیف جدید'}</h3>
                    <button className="close-btn" onClick={() => {
                      setShowDiscountForm(false);
                      setEditingDiscountCode(null);
                    }}>×</button>
                  </div>
                  <form onSubmit={handleDiscountCodeSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>کد تخفیف</label>
                        <input
                          type="text"
                          value={discountFormData.code}
                          onChange={(e) => setDiscountFormData({...discountFormData, code: e.target.value.toUpperCase()})}
                          placeholder="کد تخفیف"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>درصد تخفیف</label>
                        <input
                          type="text"
                          value={discountFormData.discountPercent}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9۰-۹.]/g, '');
                            setDiscountFormData({...discountFormData, discountPercent: value});
                          }}
                          placeholder="۱۰ (مثال: ۱٪)"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>تاریخ شروع</label>
                        <input
                          type="text"
                          value={discountFormData.startDate ? (() => {
                            try {
                              const parts = discountFormData.startDate.split('-');
                              if (parts.length === 2) {
                                const [datePart, timePart] = parts;
                                const cleanDatePart = datePart.replace(/\u202D|\u202C/g, '');
                                const cleanTimePart = timePart.replace(/\u202D|\u202C/g, '');
                                return `${cleanDatePart}-${cleanTimePart}`;
                              }
                              return discountFormData.startDate.replace(/\u202D|\u202C/g, '');
                            } catch (e) {
                              return '';
                            }
                          })() : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDiscountFormData({...discountFormData, startDate: value});
                          }}
                          className="persian-date-input"
                          placeholder="۱۴۰۴/۰۱/۰۱-۱۰:۲۰"
                          style={{
                            direction: 'ltr',
                            textAlign: 'left',
                            fontFamily: 'monospace'
                          }}
                        />
                        <small style={{color: '#666', fontSize: '12px', direction: 'ltr', textAlign: 'left'}}>فرمت: ۱۴۰۴/۰۱/۰۱-۱۰:۲۰</small>
                      </div>
                      <div className="form-group">
                        <label>تاریخ پایان</label>
                        <input
                          type="text"
                          value={discountFormData.endDate ? (() => {
                            try {
                              const parts = discountFormData.endDate.split('-');
                              if (parts.length === 2) {
                                const [datePart, timePart] = parts;
                                const cleanDatePart = datePart.replace(/\u202D|\u202C/g, '');
                                const cleanTimePart = timePart.replace(/\u202D|\u202C/g, '');
                                return `${cleanDatePart}-${cleanTimePart}`;
                              }
                              return discountFormData.endDate.replace(/\u202D|\u202C/g, '');
                            } catch (e) {
                              return '';
                            }
                          })() : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDiscountFormData({...discountFormData, endDate: value});
                          }}
                          className="persian-date-input"
                          placeholder="۱۴۰۴/۰۱/۰۱-۱۰:۲۰"
                          style={{
                            direction: 'ltr',
                            textAlign: 'left',
                            fontFamily: 'monospace'
                          }}
                        />
                        <small style={{color: '#666', fontSize: '12px', direction: 'ltr', textAlign: 'left'}}>فرمت: ۱۴۰۴/۰۱/۰۱-۱۰:۲۰</small>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>حداکثر استفاده</label>
                        <input
                          type="text"
                          value={discountFormData.maxUses}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9۰-۹]/g, '');
                            setDiscountFormData({...discountFormData, maxUses: value});
                          }}
                          placeholder="حدود (مثال: ۱۰۰)"
                        />
                      </div>
                      <div className="form-group">
                        <label>حداقل مبلغ خرید (ریال)</label>
                        <input
                          type="text"
                          value={discountFormData.minPurchaseAmount}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9۰-۹.]/g, '');
                            setDiscountFormData({...discountFormData, minPurchaseAmount: value});
                          }}
                          placeholder="بدون محدودیت (مثال: ۵۰۰۰۰)"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>توضیحات</label>
                      <textarea
                        value={discountFormData.description}
                        onChange={(e) => setDiscountFormData({...discountFormData, description: e.target.value})}
                        rows="3"
                        placeholder="توضیحات اختیاری"
                      />
                    </div>
                    <div className="form-group">
                      <label style={{display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', direction: 'rtl', width: '100%'}}>
                        <input
                          type="checkbox"
                          checked={discountFormData.isActive}
                          onChange={(e) => setDiscountFormData({...discountFormData, isActive: e.target.checked})}
                          style={{margin: 0}}
                        />
                        <span>فعال</span>
                      </label>
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingDiscountCode ? 'ذخیره تغییرات' : 'افزودن کد تخفیف'}
                      </button>
                      <button type="button" className="btn-cancel" onClick={() => {
                        setShowDiscountForm(false);
                        setEditingDiscountCode(null);
                      }} style={{width: 'auto', minWidth: 'fit-content', flex: '0 0 auto'}}>
                        انصراف
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : discountCodes.length === 0 ? (
              <p>هیچ کد تخفیفی ثبت نشده است.</p>
            ) : (
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>کد</th>
                      <th>درصد تخفیف</th>
                      <th>تاریخ شروع</th>
                      <th>تاریخ پایان</th>
                      <th>استفاده شده</th>
                      <th>وضعیت</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discountCodes.map(dc => (
                      <tr key={dc.id}>
                        <td><strong>{dc.code}</strong></td>
                        <td>{formatNumberPersian(dc.discountPercent)}%</td>
                        <td>{formatPersianDateTime(dc.startDate)}</td>
                        <td>{formatPersianDateTime(dc.endDate)}</td>
                        <td>{formatNumberPersian(dc.currentUses || 0)} / {dc.maxUses ? formatNumberPersian(dc.maxUses) : '∞'}</td>
                        <td>
                          <span className={`stock-badge ${dc.isActive ? 'in-stock' : 'out-of-stock'}`}>
                            {dc.isActive ? 'فعال' : 'غیرفعال'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-edit"
                              onClick={() => {
                                setEditingDiscountCode(dc);
                                setDiscountFormData({
                                  code: dc.code || '',
                                  discountPercent: formatNumberPersian(dc.discountPercent || ''),
                                  startDate: dc.startDate ? isoToPersianDate(dc.startDate) : '',
                                  endDate: dc.endDate ? isoToPersianDate(dc.endDate) : '',
                                  isActive: dc.isActive !== false,
                                  maxUses: formatNumberPersian(dc.maxUses || ''),
                                  minPurchaseAmount: formatNumberPersian(dc.minPurchaseAmount || ''),
                                  description: dc.description || ''
                                });
                                setShowDiscountForm(true);
                              }}
                            >
                              ویرایش
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => handleDeleteDiscountCode(dc.id)}
                            >
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'categories':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>مدیریت دسته‌بندی‌ها</h2>
              <button 
                className="btn-primary"
                onClick={() => {
                  setCategoryFormData({ name: '', description: '' });
                  setEditingCategory(null);
                  setShowCategoryForm(true);
                }}
              >
                + افزودن دسته‌بندی جدید
              </button>
            </div>

            {showCategoryForm && (
              <div className="address-form-container">
                <div className="address-form">
                  <div className="form-header">
                    <h3>{editingCategory ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی جدید'}</h3>
                    <button className="close-btn" onClick={() => {
                      setShowCategoryForm(false);
                      setEditingCategory(null);
                      setCategoryFormData({ name: '', description: '' });
                    }}>×</button>
                  </div>
                  <form onSubmit={handleCategorySubmit}>
                    <div className="form-group">
                      <label>نام دسته‌بندی</label>
                      <input
                        type="text"
                        value={categoryFormData.name}
                        onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                        placeholder="مثال: الکترونیک"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>توضیحات</label>
                      <textarea
                        value={categoryFormData.description}
                        onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})}
                        rows="3"
                        placeholder="توضیحات اختیاری"
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        {editingCategory ? 'ذخیره تغییرات' : 'افزودن دسته‌بندی'}
                      </button>
                      <button type="button" className="btn-cancel" onClick={() => {
                        setShowCategoryForm(false);
                        setEditingCategory(null);
                        setCategoryFormData({ name: '', description: '' });
                      }} style={{width: 'auto', minWidth: 'fit-content', flex: '0 0 auto'}}>
                        انصراف
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : categories.length === 0 ? (
              <p>هیچ دسته‌بندی ثبت نشده است.</p>
            ) : (
              <div className="products-table-container">
                <h3>لیست دسته‌بندی‌ها ({formatNumberPersian(categories.length)})</h3>
                <div className="products-table">
                  <table>
                    <thead>
                      <tr>
                        <th>نام</th>
                        <th>توضیحات</th>
                        <th>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map(category => (
                        <tr key={category.id}>
                          <td><strong>{category.name}</strong></td>
                          <td>{category.description || '-'}</td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="btn-edit"
                                onClick={() => handleCategoryEdit(category)}
                              >
                                ویرایش
                              </button>
                              <button 
                                className="btn-delete"
                                onClick={() => handleCategoryDelete(category.id)}
                              >
                                حذف
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'allOrders':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>مدیریت سفارشات</h2>
            </div>

            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : allOrders.length === 0 ? (
              <p>هیچ سفارشی ثبت نشده است.</p>
            ) : (
              <div className="products-table-container">
                <h3>لیست سفارشات ({formatNumberPersian(allOrders.length)})</h3>
                <div className="products-table">
                  <table>
                    <thead>
                      <tr>
                        <th>شماره سفارش</th>
                        <th>کاربر</th>
                        <th>مبلغ کل</th>
                        <th>تخفیف</th>
                        <th>وضعیت</th>
                        <th>تاریخ</th>
                        <th>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allOrders.map(order => (
                        <tr key={order.id}>
                          <td><strong>#{formatNumberPersian(order.id)}</strong></td>
                          <td>
                            <div>
                              <div>{order.user?.name || '-'}</div>
                              <div style={{fontSize: '0.85rem', color: '#666'}}>
                                {formatMobileForDisplay(order.user?.mobile)}
                              </div>
                            </div>
                          </td>
                          <td>{formatPrice(order.totalAmount)}</td>
                          <td>{order.discountAmount ? formatPrice(order.discountAmount) : '-'}</td>
                          <td>
                            <select
                              value={order.status}
                              onChange={(e) => handleOrderStatusUpdate(order.id, e.target.value)}
                              className="status-select"
                            >
                              {ORDER_STATUS_LIST.map(({ value, label }) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </td>
                          <td>{formatPersianDateShort(order.createdAt)}</td>
                          <td>
                            <button 
                              className="btn-edit"
                              onClick={() => window.open(`/invoice/${order.id}`, '_blank')}
                            >
                              مشاهده
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'payments':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>مدیریت تراکنش‌ها</h2>
            </div>

            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : payments.length === 0 ? (
              <p>هیچ تراکنشی ثبت نشده است.</p>
            ) : (
              <div className="products-table-container">
                <h3>لیست تراکنش‌ها ({formatNumberPersian(payments.length)})</h3>
                <div className="products-table">
                  <table>
                    <thead>
                      <tr>
                        <th>شماره تراکنش</th>
                        <th>شماره سفارش</th>
                        <th>کاربر</th>
                        <th>مبلغ</th>
                        <th>روش پرداخت</th>
                        <th>وضعیت</th>
                        <th>تاریخ</th>
                        <th>کد تراکنش</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(payment => (
                        <tr key={payment.id}>
                          <td><strong>#{formatNumberPersian(payment.id)}</strong></td>
                          <td>#{formatNumberPersian(payment.order?.id || '-')}</td>
                          <td>
                            <div>
                              <div>{payment.user?.name || '-'}</div>
                              <div style={{fontSize: '0.85rem', color: '#666'}}>
                                {formatMobileForDisplay(payment.user?.mobile)}
                              </div>
                            </div>
                          </td>
                          <td>{formatPrice(payment.amount)}</td>
                          <td>
                            {payment.method === 'ONLINE' ? 'آنلاین' :
                             payment.method === 'CASH_ON_DELIVERY' ? 'پرداخت در محل' :
                             payment.method === 'BANK_TRANSFER' ? 'انتقال بانکی' :
                             payment.method === 'CARD' ? 'کارت' : payment.method}
                          </td>
                          <td>
                            <select
                              value={payment.status}
                              onChange={(e) => handlePaymentStatusUpdate(payment.id, e.target.value)}
                              className="status-select"
                            >
                              <option value="PENDING">در انتظار</option>
                              <option value="COMPLETED">تکمیل شده</option>
                              <option value="FAILED">ناموفق</option>
                              <option value="CANCELLED">لغو شده</option>
                              <option value="REFUNDED">بازگشت شده</option>
                            </select>
                          </td>
                          <td>{formatPersianDateShort(payment.createdAt)}</td>
                          <td>
                            {payment.transactionId ? (
                              <div style={{fontSize: '0.85rem'}}>
                                {formatNumberPersian(payment.transactionId)}
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'files':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        return (
          <div className="dashboard-section">
            <div className="section-header">
<h2>مدیریت گالری</h2>
            <label className="btn-primary" style={{cursor: 'pointer'}}>
                + آپلود عکس جدید
                <input
                  type="file"
                  accept="image/*"
                  style={{display: 'none'}}
                  onChange={handleFileUpload}
                />
              </label>
            </div>

            {loading ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : files.length === 0 ? (
              <p>هیچ عکسی آپلود نشده است.</p>
            ) : (
              <div className="gallery-grid">
                {files.map((file, index) => (
                  <div key={index} className="gallery-item">
                    <button
                      className="gallery-item-delete-btn"
                      onClick={() => {
                        if (window.confirm('آیا قصد حذف این عکس را دارید؟')) {
                          handleFileDelete(file.filename);
                        }
                      }}
                      title="حذف"
                    >
                      ×
                    </button>
                    <img 
                      src={file.url} 
                      alt={file.filename}
                      onError={(e) => {
                        e.target.src = getPlaceholderImage('Error', 100, 100);
                      }}
                    />
                    <div className="gallery-item-size">
                      <small>{formatFileSize(file.size)}</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'gatewayTest':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        const formatAmountSeparator = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u066C');
        const handleGatewayTestAmountChange = (e) => {
          const raw = e.target.value;
          const v = raw.replace(/[۰-۹]/g, (c) => ({ '۰':'0','۱':'1','۲':'2','۳':'3','۴':'4','۵':'5','۶':'6','۷':'7','۸':'8','۹':'9' }[c] || c)).replace(/\D/g, '');
          setGatewayTestAmount(v);
        };
        const handleGatewayTestSubmit = async (e) => {
          e.preventDefault();
          const amount = parseInt(gatewayTestAmount, 10);
          if (!amount || amount < 1000) {
            showNotification('مبلغ حداقل ۱٬۰۰۰ ریال وارد کنید', 'error');
            return;
          }
          setGatewayTestLoading(true);
          try {
            const response = await apiFetch('/api/payments/zibal/test-request', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ amount })
            });
            const data = response.ok ? await response.json() : await response.json().catch(() => ({}));
            if (response.ok && data.paymentUrl) {
              window.location.href = data.paymentUrl;
              return;
            }
            showNotification(userFacingApiError(data, 'درگاه در دسترس نیست یا مبلغ نامعتبر است'), 'error');
          } catch (err) {
            showNotification('خطا در اتصال به درگاه', 'error');
          } finally {
            setGatewayTestLoading(false);
          }
        };
        return (
          <div className="dashboard-section">
            <h2>تست درگاه</h2>
            <p style={{ marginBottom: '20px', color: isDarkMode ? '#b0b0b0' : '#666' }}>
              پرداخت تستی بدون ثبت سفارش. مبلغ حداقل ۱٬۰۰۰ ریال.
            </p>
            <form onSubmit={handleGatewayTestSubmit} style={{ maxWidth: '400px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>مبلغ (ریال)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={gatewayTestAmount ? formatAmountSeparator(gatewayTestAmount) : ''}
                  onChange={handleGatewayTestAmountChange}
                  placeholder="مثال: ۱۰٬۰۰۰"
                  className="dashboard-input"
                  style={{ width: '100%', textAlign: 'left', direction: 'ltr' }}
                />
              </div>
              <button type="submit" className="btn-primary" disabled={gatewayTestLoading}>
                {gatewayTestLoading ? 'در حال انتقال...' : 'انتقال به درگاه پرداخت'}
              </button>
            </form>
          </div>
        );

      case 'paymentInquiry':
        if (!isProgrammerOrAdmin) return <div>دسترسی غیرمجاز</div>;
        const STATUS_LABELS = { '-1': 'در انتظار پرداخت', '-2': 'خطای داخلی', '1': 'پرداخت شده - تاییدشده', '2': 'پرداخت شده - تاییدنشده', '3': 'لغوشده توسط کاربر', '4': 'شماره کارت نامعتبر', '5': 'موجودی کافی نیست', '6': 'رمز اشتباه', '7': 'تعداد درخواست بیش از حد', '8': 'تعداد پرداخت روزانه بیش از حد', '9': 'مبلغ پرداخت روزانه بیش از حد', '10': 'صادرکننده نامعتبر', '11': 'خطای سوییچ', '12': 'کارت قابل دسترسی نیست', '15': 'استرداد شده', '16': 'در حال استرداد', '18': 'ریورس شده', '21': 'پذیرنده نامعتبر' };
        const handleInquiry = async (e) => {
          e.preventDefault();
          const tid = inquiryTrackId.trim();
          if (!tid) {
            showNotification('شناسه پیگیری (trackId) را وارد کنید', 'error');
            return;
          }
          setInquiryLoading(true);
          setInquiryResult(null);
          try {
            const response = await apiFetch('/api/payments/zibal/inquiry', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ trackId: tid })
            });
            let data;
            try {
              data = await response.json();
            } catch {
              data = { result: -1, message: 'پاسخ نامعتبر از سرور' };
            }
            setInquiryResult(data);
            if (!response.ok) showNotification('استعلام ناموفق', 'error');
          } catch (err) {
            showNotification('خطا در استعلام', 'error');
            setInquiryResult({ result: -1, message: 'خطا در ارتباط با سرور' });
          } finally {
            setInquiryLoading(false);
          }
        };
        const res = inquiryResult;
        const resultOk = res && res.result === 100;
        return (
          <div className="dashboard-section">
            <h2>استعلام پرداخت</h2>
            <p style={{ marginBottom: '20px', color: isDarkMode ? '#b0b0b0' : '#666' }}>
              با وارد کردن شناسه پیگیری (trackId) وضعیت تراکنش را از درگاه زیبال دریافت کنید.
            </p>
            <form onSubmit={handleInquiry} style={{ maxWidth: '400px', marginBottom: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>شناسه پیگیری (trackId)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={inquiryTrackId}
                  onChange={(e) => setInquiryTrackId(e.target.value)}
                  placeholder="مثال: 15966442233311"
                  className="dashboard-input"
                  style={{ width: '100%', textAlign: 'left', direction: 'ltr' }}
                />
              </div>
              <button type="submit" className="btn-primary" disabled={inquiryLoading}>
                {inquiryLoading ? 'در حال استعلام...' : 'استعلام'}
              </button>
            </form>
            {res && (
              <div style={{ maxWidth: '500px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', background: isDarkMode ? '#1a1a1a' : '#f9f9f9', textAlign: 'right' }}>
                <div style={{ marginBottom: '12px', fontWeight: 600, color: resultOk ? '#155724' : '#721c24' }}>
                  {resultOk
                    ? '✓ گزارش تراکنش'
                    : `خطا: ${persianOrNull(res.message) || STATUS_LABELS[String(res.status)] || (res.result != null ? `کد نتیجه: ${res.result}` : 'استعلام ناموفق')}`}
                </div>
                {resultOk && (
                  <>
                    {res.amount != null && <p><strong>مبلغ:</strong> {formatPrice(res.amount)}</p>}
                    {res.status != null && <p><strong>وضعیت:</strong> {STATUS_LABELS[String(res.status)] || res.status}</p>}
                    {res.refNumber != null && <p><strong>شناسه مرجع:</strong> {res.refNumber}</p>}
                    {res.cardNumber != null && <p><strong>کارت:</strong> {res.cardNumber}</p>}
                    {res.orderId != null && <p><strong>شناسه سفارش:</strong> {res.orderId}</p>}
                    {res.description != null && <p><strong>توضیحات:</strong> {res.description}</p>}
                    {res.createdAt != null && <p><strong>تاریخ ایجاد:</strong> {res.createdAt}</p>}
                    {res.paidAt != null && <p><strong>تاریخ پرداخت:</strong> {res.paidAt}</p>}
                    {res.verifiedAt != null && <p><strong>تاریخ تایید:</strong> {res.verifiedAt}</p>}
                  </>
                )}
              </div>
            )}
          </div>
        );

      case 'siteSettings':
        if (!isProgrammer) return <div>دسترسی غیرمجاز</div>;
        
        const handleSaveSetting = async (setting) => {
          setLoading(true);
          try {
            const response = await apiFetch('/api/site-settings/admin', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                key: setting.key,
                value: settingValue,
                description: setting.description
              })
            });

            if (response.ok) {
              const data = await response.json();
              showNotification('تنظیمات با موفقیت به‌روزرسانی شد', 'success');
              setEditingSetting(null);
              setSettingValue('');
// update settings in state keeping order (by key)
              setSiteSettings(prev => prev.map(s => s.key === setting.key ? data.setting : s));
// refresh settings in Context for site-wide use
              refreshSettings();
            } else {
              let msg = 'خطا در به‌روزرسانی تنظیمات';
              try {
                const errorData = await response.json();
                msg = userFacingApiError(errorData, msg);
              } catch {
                /* ignore */
              }
              showNotification(msg, 'error');
            }
          } catch (error) {
            console.error('Error updating setting:', error);
            showNotification('خطا در به‌روزرسانی تنظیمات', 'error');
          } finally {
            setLoading(false);
          }
        };

        const handleSelectFromGallery = (setting) => {
          setEditingSetting(setting);
          setGallerySelectionMode(true);
          setShowGalleryModal(true);
        };

        return (
          <div className="dashboard-section">
            <h2>تنظیمات سایت</h2>
            {loading && !editingSetting ? (
              <div className="loading">در حال بارگذاری...</div>
            ) : (
              <div>
                {siteSettings.map(setting => (
                  <div key={setting.key} style={{marginBottom: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', background: isDarkMode ? '#1a1a1a' : '#fff'}}>
                    <h3 style={{marginBottom: '15px', color: isDarkMode ? '#e0e0e0' : '#333'}}>
                      {setting.description || setting.key}
                    </h3>
                    {editingSetting?.key === setting.key ? (
                      <div>
                        {(setting.key === 'favicon_url' || setting.key === 'logo_url') ? (
                          <div>
                            <div style={{marginBottom: '10px'}}>
                              <input
                                type="text"
                                value={settingValue}
                                onChange={(e) => setSettingValue(e.target.value)}
                                placeholder="URL عکس"
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  borderRadius: '5px',
                                  border: '1px solid #ddd',
                                  marginBottom: '10px'
                                }}
                              />
                            </div>
                            <button
                              className="btn-primary"
                              onClick={() => handleSelectFromGallery(setting)}
                              style={{marginBottom: '10px', marginLeft: '10px', width: 'auto', flex: '0 0 auto'}}
                            >
                              انتخاب از گالری
                            </button>
                            {settingValue && (
                              <div style={{marginBottom: '10px'}}>
                                <img 
                                  src={getImageUrl(settingValue)} 
                                  alt="Preview" 
                                  style={{maxWidth: '200px', maxHeight: '200px', borderRadius: '5px'}}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              </div>
                            )}
                          </div>
                        ) : setting.key === 'bale_bot_token' ? (
                          <input
                            type="password"
                            value={settingValue}
                            onChange={(e) => setSettingValue(e.target.value)}
                            placeholder="توکن ربات از پنل توسعه‌دهندگان بله"
                            autoComplete="off"
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '5px',
                              border: '1px solid #ddd',
                              marginBottom: '10px',
                              fontFamily: 'inherit'
                            }}
                          />
                        ) : (
                          <textarea
                            value={settingValue}
                            onChange={(e) => setSettingValue(e.target.value)}
                            placeholder={setting.key === 'bale_admin_chat_ids' ? 'مثال: 11111, 22222, 33333' : 'مقدار تنظیمات'}
                            rows={setting.key === 'about_us' || setting.key === 'support' ? 6 : setting.key === 'bale_admin_chat_ids' ? 2 : 3}
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '5px',
                              border: '1px solid #ddd',
                              marginBottom: '10px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                        )}
                        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                          <button
                            className="btn-primary"
                            onClick={() => handleSaveSetting(setting)}
                            disabled={loading}
                            style={{width: 'auto', flex: '0 0 auto'}}
                          >
                            {loading ? 'در حال ذخیره...' : 'ذخیره'}
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={() => {
                              setEditingSetting(null);
                              setSettingValue('');
                            }}
                            disabled={loading}
                            style={{width: 'auto', flex: '0 0 auto'}}
                          >
                            انصراف
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p style={{marginBottom: '15px', color: isDarkMode ? '#b0b0b0' : '#666'}}>
                          {setting.key === 'bale_bot_token' && setting.value
                            ? '•••••••• (مقدار ذخیره شده؛ برای تغییر «ویرایش» را بزنید)'
                            : (setting.value || <span style={{color: '#999'}}>خالی</span>)}
                        </p>
                        {(setting.key === 'favicon_url' || setting.key === 'logo_url') && setting.value && (
                          <div style={{marginBottom: '15px'}}>
                            <img 
                              src={getImageUrl(setting.value)} 
                              alt={setting.description}
                              style={{maxWidth: '200px', maxHeight: '200px', borderRadius: '5px', border: '1px solid #ddd'}}
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                        <button
                          className="btn-primary"
                          onClick={() => {
                            setEditingSetting(setting);
                            setSettingValue(setting.value || '');
                          }}
                          style={{width: 'auto', flex: '0 0 auto'}}
                        >
                          ویرایش
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className={`dashboard-container ${isDarkMode ? 'dark' : ''}`}>
      <div className="dashboard-header">
        <div className="header-left">
          <button 
            className="hamburger-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title="منو"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18M3 6h18M3 18h18"></path>
            </svg>
          </button>
          <h1>پنل کاربری ولوریا</h1>
        </div>
        <div className="header-actions">
          <button 
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={isDarkMode ? 'روشن کردن تم' : 'تاریک کردن تم'}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          <Link 
            to="/"
            className="btn-shop-link"
          >
            رفتن به فروشگاه
          </Link>
        </div>
      </div>

      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      <div className="dashboard-layout">
        <div className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <nav className="dashboard-nav">
            <button 
              className={activeTab === 'profile' ? 'active' : ''}
              onClick={() => {
                setActiveTab('profile');
                closeSidebarIfMobile();
              }}
            >
              پروفایل من
            </button>
            <button 
              className={activeTab === 'orders' ? 'active' : ''}
              onClick={() => {
                setActiveTab('orders');
                closeSidebarIfMobile();
              }}
            >
              سفارش‌های من
            </button>
            <button 
              className={activeTab === 'addresses' ? 'active' : ''}
              onClick={() => {
                setActiveTab('addresses');
                closeSidebarIfMobile();
              }}
            >
              آدرس‌های من
            </button>
            {isProgrammerOrAdmin && (
              <>
                <button 
                  className={activeTab === 'products' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('products');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت محصولات
                </button>
                <button 
                  className={activeTab === 'users' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('users');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت کاربران
                </button>
                <button 
                  className={activeTab === 'discountCodes' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('discountCodes');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت کدهای تخفیف
                </button>
                <button 
                  className={activeTab === 'categories' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('categories');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت دسته‌بندی‌ها
                </button>
                <button 
                  className={activeTab === 'allOrders' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('allOrders');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت سفارشات
                </button>
                <button 
                  className={activeTab === 'payments' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('payments');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت تراکنش‌ها
                </button>
                <button 
                  className={activeTab === 'paymentInquiry' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('paymentInquiry');
                    closeSidebarIfMobile();
                  }}
                >
                  استعلام پرداخت
                </button>
                <button 
                  className={activeTab === 'gatewayTest' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('gatewayTest');
                    closeSidebarIfMobile();
                  }}
                >
                  تست درگاه
                </button>
                <button
                  className={activeTab === 'files' ? 'active' : ''}
                  onClick={() => {
                    setActiveTab('files');
                    closeSidebarIfMobile();
                  }}
                >
                  مدیریت گالری
                </button>
              </>
            )}
            {isProgrammer && (
              <button 
                className={activeTab === 'siteSettings' ? 'active' : ''}
                onClick={() => {
                  setActiveTab('siteSettings');
                  closeSidebarIfMobile();
                }}
              >
                تنظیمات سایت
              </button>
            )}
            <button 
              type="button"
              className="logout-nav-btn"
              onClick={() => {
                handleLogout();
                closeSidebarIfMobile();
              }}
            >
              خروج
            </button>
          </nav>
        </div>

        <div className="dashboard-main">
          {renderContent()}
        </div>
      </div>

      {showGalleryModal && (
        <div className="gallery-modal-overlay" onClick={() => {
          setShowGalleryModal(false);
          setGallerySelectionMode(false);
        }}>
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-modal-header">
              <h3>{gallerySelectionMode ? 'انتخاب عکس برای تنظیمات' : 'انتخاب عکس از گالری'}</h3>
              <button 
                className="gallery-modal-close"
                onClick={() => {
                  setShowGalleryModal(false);
                  setGallerySelectionMode(false);
                }}
              >
                ×
              </button>
            </div>
            <div className="gallery-modal-content">
              {loading ? (
                <div className="loading">در حال بارگذاری...</div>
              ) : files.length === 0 ? (
                <p>هیچ عکسی در گالری وجود ندارد.</p>
              ) : (
                <div className="gallery-selection-grid">
                  {files.map((file, index) => {
                    const fileUrl = getImageUrl(file.url); // Normalize URL to relative path
                    const isSelected = gallerySelectionMode ? false : productImages.some(img => getImageUrl(img.imageUrl) === fileUrl);
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (gallerySelectionMode) {
// select for site settings
                            if (editingSetting) {
                              setSettingValue(fileUrl);
                              setShowGalleryModal(false);
                              setGallerySelectionMode(false);
                              showNotification('عکس انتخاب شد', 'success');
                            }
                          } else {
// select for products
                            if (!isSelected) {
                              setProductImages(prev => [...prev, { imageUrl: fileUrl }]);
                              showNotification('عکس اضافه شد', 'success');
                            } else {
                              setProductImages(prev => prev.filter(img => img.imageUrl !== fileUrl));
                              showNotification('عکس حذف شد', 'success');
                            }
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          border: isSelected ? '3px solid #0C8EF4' : '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          padding: '2px',
                          position: 'relative',
                          transition: 'all 0.3s'
                        }}
                      >
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            background: '#0C8EF4',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 10
                          }}>✓</div>
                        )}
                        <div style={{
                          width: '100%',
                          aspectRatio: '1',
                          background: isDarkMode 
                            ? 'repeating-conic-gradient(#2a2a2a 0% 25%, #1a1a1a 0% 50%) 50% / 10px 10px, repeating-conic-gradient(#2a2a2a 0% 25%, #1a1a1a 0% 50%) 50% / 10px 10px'
                            : 'repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 50% / 10px 10px, repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 50% / 10px 10px',
                          backgroundColor: isDarkMode ? '#0a0a0a' : '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img 
                            src={fileUrl}
                            alt={file.filename}
                            style={{width: '100%', height: '100%', objectFit: 'contain', background: 'transparent'}}
                            onError={(e) => {
                              e.target.src = getPlaceholderImage('Error', 150, 150);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="gallery-modal-actions">
              <button 
                type="button"
                className="btn-primary"
                onClick={() => {
                  setShowGalleryModal(false);
                  setGallerySelectionMode(false);
                }}
              >
                {gallerySelectionMode ? 'انصراف' : 'بستن'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutDialog && (
        <div className="logout-dialog-overlay" onClick={cancelLogout}>
          <div className="logout-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="logout-dialog-header">
              <h3>تأیید خروج</h3>
            </div>
            <div className="logout-dialog-content">
              <p>آیا قصد خروج دارید؟</p>
            </div>
            <div className="logout-dialog-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={cancelLogout}
                style={{width: 'auto', minWidth: 'fit-content', flex: '0 0 auto'}}
              >
                خیر
              </button>
              <button 
                type="button"
                className="btn-logout-confirm"
                onClick={confirmLogout}
              >
                بله
              </button>
            </div>
          </div>
        </div>
      )}

      {showCropModal && cropImage && (
        <div className="crop-modal-overlay" onClick={() => {
          setShowCropModal(false);
          setCropImage(null);
          setCropImageType(null);
          setCrop({ unit: '%', width: 90, height: 90 });
          setCompletedCrop(null);
        }}>
          <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crop-modal-header">
              <h3>کروپ عکس</h3>
              <button 
                className="crop-modal-close"
                onClick={() => {
                  setShowCropModal(false);
                  setCropImage(null);
                  setCropImageType(null);
                  setCrop({ unit: '%', width: 90, height: 90 });
                  setCompletedCrop(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="crop-modal-content">
              <ReactCrop
                src={cropImage}
                crop={crop}
                onChange={(newCrop) => setCrop(newCrop)}
                onComplete={(c) => setCompletedCrop(c)}
              >
                <img
                  ref={imgRef}
                  src={cropImage}
                  alt="Crop"
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                  onLoad={() => {
                    if (imgRef.current) {
                      const { width, height } = imgRef.current;
                      const size = Math.min(width, height) * 0.9;
                      setCrop({
                        unit: 'px',
                        width: size,
                        height: size,
                        x: (width - size) / 2,
                        y: (height - size) / 2,
                      });
                    }
                  }}
                />
              </ReactCrop>
            </div>
            <div className="crop-modal-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowCropModal(false);
                  setCropImage(null);
                  setCropImageType(null);
                  setCrop({ unit: '%', width: 90, height: 90 });
                  setCompletedCrop(null);
                }}
                style={{width: 'auto', minWidth: 'fit-content', flex: '0 0 auto'}}
              >
                انصراف
              </button>
              <button 
                type="button"
                className="btn-primary"
                onClick={handleCropComplete}
                disabled={loading || !completedCrop}
              >
                {loading ? 'در حال آپلود...' : 'تأیید و آپلود'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;

